import React, { useMemo, useState } from "react";
import {
  ArrowLeft, Trash, Tag, CheckCircle, Warning, MinusCircle, Circle,
  Lightning, FileText, ArrowRight, Plus, Eye, ChatCircle, CaretDown,
  ShieldCheck, ShieldWarning, Brain, UploadSimple, Link as LinkIcon,
} from "@phosphor-icons/react";
import { evidenceElements, bodySystems, conditionLibrary, getConditionResources } from "@/lib/claimData";
import { nexusWaiver } from "@/lib/nexusWaiver";
import { flagShortService } from "@/lib/shortServiceFlag";
import { analyzeIssueRisk } from "@/lib/riskEngine";
import { THEORIES, THEORY_SHORT_LABEL } from "@/lib/claimEnums";
import {
  PILLAR_STATE, PILLAR_LABEL, PILLAR_CLASS,
  pillarStateForIssue, getNextBestAction,
} from "@/lib/pillarStatusV2";

const STATE_ICON = {
  [PILLAR_STATE.complete]: <CheckCircle size={14} weight="fill" />,
  [PILLAR_STATE.needsReview]: <Warning size={14} weight="fill" />,
  [PILLAR_STATE.missing]: <MinusCircle size={14} weight="fill" />,
  [PILLAR_STATE.notStarted]: <Circle size={14} weight="bold" />,
};

const PILLAR_ICON = { current: FileText, service: Tag, nexus: Lightning, severity: ChatCircle };

function StatusPill({ state, testid }) {
  return (
    <span className={PILLAR_CLASS[state]} data-testid={testid} data-state={state}>
      {STATE_ICON[state]}
      <span>{PILLAR_LABEL[state]}</span>
    </span>
  );
}

function ReadinessChecklist({ issue, evidence, waiverCtx }) {
  return (
    <div className="readiness-checklist" data-testid={`readiness-checklist-${issue.id}`}>
      {evidenceElements.map((el) => {
        const state = pillarStateForIssue(issue, el.id, evidence, waiverCtx);
        return (
          <div key={el.id} className="readiness-line" data-state={state}>
            {STATE_ICON[state]}
            <span className="readiness-line-label">{el.label}</span>
            <span className="readiness-line-state">{PILLAR_LABEL[state]}</span>
          </div>
        );
      })}
    </div>
  );
}

function PreExistingFlagBanner({ issue, master526ez, onSetAggravationTheory }) {
  const flag = useMemo(() => flagShortService(master526ez), [master526ez]);
  if (!flag.shortService) return null;
  const alreadyAggravation = issue.theory === THEORIES.aggravation;
  return (
    <div
      className={`preexisting-banner preexisting-banner--${flag.severity}`}
      data-testid={`preexisting-banner-${issue.id}`}
      data-severity={flag.severity}
    >
      <div className="preexisting-banner-icon"><Warning size={18} weight="fill" /></div>
      <div className="preexisting-banner-body">
        <div className="preexisting-banner-eyebrow mono">
          {flag.veryShort ? "PRE-EXISTING CONDITION SCRUTINY LIKELY" : "PRE-EXISTING CONDITION CHECK"}
        </div>
        <div className="preexisting-banner-headline" data-testid={`preexisting-headline-${issue.id}`}>
          {flag.headline}
        </div>
        <div className="preexisting-banner-copy" data-testid={`preexisting-copy-${issue.id}`}>
          {flag.reason}
        </div>
        {!alreadyAggravation && (
          <button
            type="button"
            className="btn btn--outline btn--xs preexisting-banner-cta"
            onClick={onSetAggravationTheory}
            data-testid={`preexisting-set-theory-${issue.id}`}
          >
            Switch theory to &ldquo;Aggravation of pre-existing condition&rdquo;
          </button>
        )}
        {alreadyAggravation && (
          <div className="preexisting-banner-confirm" data-testid={`preexisting-confirmed-${issue.id}`}>
            <CheckCircle size={12} weight="fill" /> Theory is already set to aggravation.
          </div>
        )}
      </div>
    </div>
  );
}

const LEVEL_TONE = {
  Low:      { bg: "linear-gradient(95deg, rgba(16,185,129,0.10), rgba(16,185,129,0.04))", border: "rgba(16,185,129,0.32)", text: "#047857", chip: "#16a34a" },
  Moderate: { bg: "linear-gradient(95deg, rgba(217,119,6,0.10), rgba(217,119,6,0.04))",  border: "rgba(217,119,6,0.32)",  text: "#b45309", chip: "#d97706" },
  High:     { bg: "linear-gradient(95deg, rgba(220,38,38,0.10), rgba(220,38,38,0.04))",  border: "rgba(220,38,38,0.32)",  text: "#b91c1c", chip: "#dc2626" },
};

function RiskCard({ kind, level, reasons, testid, icon: Icon }) {
  const tone = LEVEL_TONE[level] || LEVEL_TONE.Low;
  const title = kind === "sc" ? "Service Connection Risk" : "Rating / Severity Risk";
  return (
    <div
      className="risk-card"
      data-testid={testid}
      data-level={level}
      style={{
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 14,
        padding: "14px 16px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon size={16} weight="fill" color={tone.chip} />
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", color: tone.text }}>
            {title.toUpperCase()}
          </div>
        </div>
        <span
          data-testid={`${testid}-level`}
          style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
            padding: "3px 10px", borderRadius: 999,
            background: tone.chip, color: "#fff",
          }}
        >{level}</span>
      </div>
      {reasons.length === 0 ? (
        <div style={{ marginTop: 8, fontSize: 12.5, color: tone.text, fontWeight: 600 }}>
          No issues detected on this side.
        </div>
      ) : (
        <ul style={{ margin: "8px 0 0", paddingLeft: 16, color: "#0f172a" }}>
          {reasons.map((r) => (
            <li key={r.kind} data-testid={`${testid}-reason-${r.kind}`} style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 2 }}>
              {r.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NexusCoveredNote({ issue, coverage, source, longReason }) {
  if (!coverage || coverage.state !== "have") return null;
  // Only show the note when nexus is covered indirectly (waiver or in-service evidence)
  if (!source || source === "attached") return null;
  return (
    <div
      data-testid={`nexus-covered-${issue.id}`}
      style={{
        marginTop: 10,
        padding: "10px 12px",
        background: "rgba(16,185,129,0.08)",
        border: "1px solid rgba(16,185,129,0.32)",
        borderRadius: 12,
        display: "flex", gap: 10, alignItems: "flex-start",
      }}
    >
      <CheckCircle size={16} weight="fill" color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#047857", letterSpacing: "0.08em" }}>NEXUS / LINK - COVERED</div>
        <div style={{ fontSize: 13, color: "#0f172a", marginTop: 2, fontWeight: 600 }}>
          {source === "waiver" ? "Waived by claim posture." : "Covered by in-service documentation."}
        </div>
        <div style={{ fontSize: 12, color: "#475569", marginTop: 4, lineHeight: 1.5 }}>
          {longReason}
        </div>
      </div>
    </div>
  );
}

function RiskSummaryBox({ analysis, issue }) {
  const isComplete = analysis.summaryHeadline === "Package looks complete";
  const isSecondaryGap = analysis.needsPrimaryForSecondary;
  const tone = isComplete
    ? { bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.32)", iconColor: "#16a34a", Icon: ShieldCheck }
    : isSecondaryGap
      ? { bg: "rgba(220,38,38,0.06)", border: "rgba(220,38,38,0.30)", iconColor: "#dc2626", Icon: ShieldWarning }
      : { bg: "rgba(15,42,81,0.04)", border: "rgba(15,42,81,0.20)", iconColor: "#0F2A51", Icon: ShieldWarning };
  const ToneIcon = tone.Icon;
  return (
    <div
      data-testid={`risk-summary-${issue.id}`}
      style={{
        marginTop: 12,
        padding: "14px 16px",
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 14,
        display: "flex", gap: 12, alignItems: "flex-start",
      }}
    >
      <ToneIcon size={20} weight="duotone" color={tone.iconColor} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div data-testid={`risk-summary-headline-${issue.id}`} style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
          {analysis.summaryHeadline}
        </div>
        <div data-testid={`risk-summary-detail-${issue.id}`} style={{ fontSize: 12.5, color: "#475569", marginTop: 6, lineHeight: 1.55 }}>
          {analysis.detailMessage}
        </div>
      </div>
    </div>
  );
}

function SecondaryPrimaryPicker({ issue, allIssues, updateIssue }) {
  const isSecondary = (issue.theory || "").toLowerCase().includes("secondary");
  if (!isSecondary) return null;
  const others = allIssues.filter((i) => i.id !== issue.id);
  const selected = issue.secondaryToIssueId || "";
  const missing = !selected && !(issue.secondaryTo || "").trim();
  return (
    <div
      data-testid={`secondary-picker-${issue.id}`}
      style={{
        marginTop: 12,
        padding: "12px 14px",
        background: missing ? "rgba(220,38,38,0.06)" : "rgba(15,42,81,0.04)",
        border: `1px solid ${missing ? "rgba(220,38,38,0.30)" : "rgba(15,42,81,0.18)"}`,
        borderRadius: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <LinkIcon size={14} weight="bold" color={missing ? "#dc2626" : "#0F2A51"} />
        <div style={{ fontSize: 11, fontWeight: 700, color: missing ? "#b91c1c" : "#0F2A51", letterSpacing: "0.08em" }}>
          PRIMARY CONDITION THIS IS SECONDARY TO
        </div>
      </div>
      {missing && (
        <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 6, lineHeight: 1.5 }}>
          Secondary theory incomplete - select the primary service-connected condition or change theory to Direct / BDD.
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <select
          className="field-input"
          data-testid={`secondary-primary-select-${issue.id}`}
          value={selected}
          onChange={(e) => {
            const id = e.target.value;
            const primary = others.find((o) => o.id === id);
            updateIssue(issue.id, {
              secondaryToIssueId: id,
              secondaryTo: primary ? primary.claimedAs : "",
            });
          }}
          style={{ flex: 1, minWidth: 220, fontSize: 13 }}
        >
          <option value="">- Select primary condition -</option>
          {others.map((o) => (
            <option key={o.id} value={o.id}>{o.claimedAs} (DC {o.code})</option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          data-testid={`secondary-switch-direct-${issue.id}`}
          onClick={() => updateIssue(issue.id, { theory: THEORIES.direct, secondaryToIssueId: "", secondaryTo: "" })}
        >
          Switch to Direct / BDD
        </button>
      </div>
    </div>
  );
}

const MENTAL_HEALTH_PROMPTS = [
  "Frequency of symptoms",
  "Severity of symptoms",
  "Duration of symptoms",
  "Occupational impairment",
  "Social impairment",
  "Sleep impairment",
  "Medication use",
  "Panic attacks",
  "Suicidal ideation (if present)",
  "Difficulty adapting to stressful circumstances",
  "Deficiencies in work, family relations, judgment, thinking, or mood",
];

function MentalHealthHelper({ issue }) {
  if ((issue.system || "").toLowerCase() !== "mental") return null;
  return (
    <div
      data-testid={`mh-helper-${issue.id}`}
      style={{
        marginTop: 12,
        padding: "14px 16px",
        background: "linear-gradient(95deg, rgba(124,58,237,0.08), rgba(124,58,237,0.02))",
        border: "1px solid rgba(124,58,237,0.30)",
        borderRadius: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Brain size={16} weight="fill" color="#7C3AED" />
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", color: "#6D28D9" }}>
          MENTAL HEALTH RATING HELPER
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginTop: 6 }}>
        Mental-health ratings (38 CFR Sec. 4.130) are driven by the General Rating Formula. Document each of these in your personal statement, treatment notes, or symptom log:
      </div>
      <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#1e1b4b", columns: 2, columnGap: 24 }}>
        {MENTAL_HEALTH_PROMPTS.map((p) => (
          <li key={p} data-testid={`mh-prompt-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} style={{ fontSize: 12.5, lineHeight: 1.6, marginBottom: 2, breakInside: "avoid" }}>
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecommendedNextUploadCard({ issue, recommendations }) {
  if (!recommendations || recommendations.length === 0) return null;
  return (
    <div
      data-testid={`recommended-uploads-${issue.id}`}
      style={{
        marginTop: 12,
        padding: "14px 16px",
        background: "rgba(15,42,81,0.04)",
        border: "1px solid rgba(15,42,81,0.20)",
        borderRadius: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <UploadSimple size={16} weight="bold" color="#0F2A51" />
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", color: "#0F2A51" }}>
          RECOMMENDED NEXT UPLOAD
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: "#475569", marginTop: 4 }}>
        For this condition, the next upload should be:
      </div>
      <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#0f172a" }}>
        {recommendations.map((r, i) => (
          <li key={r} data-testid={`rec-upload-${i}`} style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 2 }}>
            {r}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ClaimRiskSection({ issue, allIssues, evidence, waiverCtx, updateIssue }) {
  const analysis = useMemo(
    () => analyzeIssueRisk(issue, allIssues, evidence, waiverCtx),
    [issue, allIssues, evidence, waiverCtx],
  );
  return (
    <section className="simple-cd-risk" data-testid={`risk-section-${issue.id}`}>
      <div className="simple-cd-section-head">
        <h3 className="simple-cd-section-title">Claim risk check</h3>
        <span className="simple-cd-section-hint">Service connection vs. rating - analysed separately</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="risk-cards-grid">
        <RiskCard
          kind="sc"
          level={analysis.serviceConnection.level}
          reasons={analysis.serviceConnection.reasons}
          testid={`risk-sc-${issue.id}`}
          icon={ShieldCheck}
        />
        <RiskCard
          kind="rating"
          level={analysis.rating.level}
          reasons={analysis.rating.reasons}
          testid={`risk-rating-${issue.id}`}
          icon={ShieldWarning}
        />
      </div>
      <NexusCoveredNote
        issue={issue}
        coverage={analysis.coverage.nexus}
        source={analysis.nexus?.source}
        longReason={analysis.nexus?.longReason}
      />
      <SecondaryPrimaryPicker issue={issue} allIssues={allIssues} updateIssue={updateIssue} />
      <RiskSummaryBox analysis={analysis} issue={issue} />
      <MentalHealthHelper issue={issue} />
      <RecommendedNextUploadCard issue={issue} recommendations={analysis.recommendedNextUpload} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 2 — BIGGEST RISK HERO
//
// One hero card that picks the SINGLE most important thing to know about
// THIS condition right now, plus a tight list of supporting secondary
// concerns. Replaces the old layout where Pre-existing-banner + NBA + dual
// risk cards + Mental Health helper all sat at equal visual weight competing
// for attention.
// ---------------------------------------------------------------------------

function pickPrimaryRisk(analysis, shortServiceFlagResult) {
  // Highest-impact issues first. Each branch returns ONE primary, and the
  // remaining concerns are demoted to the secondary list.
  if (analysis.needsPrimaryForSecondary) {
    return {
      key: "secondary-incomplete",
      severity: "high",
      eyebrow: "ACTION REQUIRED",
      headline: "Secondary theory incomplete",
      why: "VA can't grant a secondary claim without a primary service-connected condition to attach it to. Until that link is set, this claim sits in limbo regardless of how strong the rest of the evidence is.",
      detail: analysis.detailMessage,
    };
  }
  if (analysis.serviceConnection.level === "High") {
    return {
      key: "sc-high",
      severity: "high",
      eyebrow: "BIGGEST RISK",
      headline: "Service connection is not yet supported",
      why: "Without all three service-connection pillars (current diagnosis, in-service event, and a link between them), VA will deny the claim regardless of how severe the symptoms are today.",
      detail: analysis.detailMessage,
    };
  }
  if (analysis.serviceConnection.level === "Moderate") {
    return {
      key: "sc-moderate",
      severity: "moderate",
      eyebrow: "BIGGEST RISK",
      headline: "Service connection has gaps",
      why: "Service connection looks workable but isn't airtight. Closing the remaining gaps protects against an avoidable denial.",
      detail: analysis.detailMessage,
    };
  }
  if (analysis.rating.level === "High" || analysis.rating.level === "Moderate") {
    return {
      key: "rating",
      severity: analysis.rating.level === "High" ? "high" : "moderate",
      eyebrow: "BIGGEST RISK",
      headline: "Rating strength is weak",
      why: "Service connection looks supported. The risk now is the percentage rating — VA pays based on functional impact, and without strong severity evidence the rating tier may be lower than the actual impairment.",
      detail: analysis.detailMessage,
    };
  }
  if (shortServiceFlagResult?.shortService) {
    return {
      key: "preexisting",
      severity: "moderate",
      eyebrow: "BIGGEST RISK",
      headline: shortServiceFlagResult.headline,
      why: "VA raters scrutinise short-service claims for pre-existing conditions. Documenting worsening (entrance vs separation physicals + a lay statement) keeps the presumption of soundness intact.",
      detail: shortServiceFlagResult.reason,
    };
  }
  return {
    key: "ready",
    severity: "low",
    eyebrow: "STATUS",
    headline: "This condition looks filing-ready",
    why: "Service connection and rating strength both check out on the evidence currently on file. Final review of statements and the C&P prep sheet recommended before submitting.",
    detail: analysis.detailMessage,
  };
}

function BiggestRiskHero({ issue, allIssues, evidence, waiverCtx, updateIssue, master526ez }) {
  const analysis = useMemo(
    () => analyzeIssueRisk(issue, allIssues, evidence, waiverCtx),
    [issue, allIssues, evidence, waiverCtx],
  );
  const shortService = useMemo(() => flagShortService(master526ez), [master526ez]);
  const primary = pickPrimaryRisk(analysis, shortService);

  // Secondary risks: everything else worth flagging on this condition,
  // demoted under a small heading so they don't compete with the primary.
  const secondaries = [];
  if (primary.key !== "sc-high" && primary.key !== "sc-moderate" && analysis.serviceConnection.level !== "Low") {
    secondaries.push({ title: "Service connection gaps remain", body: analysis.serviceConnection.reasons.map((r) => r.label).join("; ") });
  }
  if (primary.key !== "rating" && analysis.rating.level !== "Low") {
    secondaries.push({ title: "Rating strength could be stronger", body: analysis.rating.reasons.map((r) => r.label).join("; ") });
  }
  if (primary.key !== "secondary-incomplete" && analysis.needsPrimaryForSecondary) {
    secondaries.push({ title: "Secondary link not yet set", body: "Select the primary service-connected condition or switch to Direct / BDD." });
  }
  if (primary.key !== "preexisting" && shortService?.shortService) {
    secondaries.push({ title: shortService.headline, body: "Document worsening from entrance to separation. See the Evidence Pillars section." });
  }

  const sevTone = {
    high: { bg: "linear-gradient(95deg, rgba(220,38,38,0.10), rgba(220,38,38,0.04))", border: "rgba(220,38,38,0.32)", chip: "#dc2626", text: "#b91c1c", Icon: ShieldWarning },
    moderate: { bg: "linear-gradient(95deg, rgba(217,119,6,0.10), rgba(217,119,6,0.04))", border: "rgba(217,119,6,0.32)", chip: "#d97706", text: "#b45309", Icon: ShieldWarning },
    low: { bg: "linear-gradient(95deg, rgba(16,185,129,0.10), rgba(16,185,129,0.04))", border: "rgba(16,185,129,0.32)", chip: "#16a34a", text: "#047857", Icon: ShieldCheck },
  }[primary.severity];
  const HeroIcon = sevTone.Icon;

  return (
    <section className="simple-cd-biggest-risk" data-testid={`biggest-risk-${issue.id}`}>
      <div className="simple-cd-section-head">
        <h3 className="simple-cd-section-title">Biggest risk</h3>
        <span className="simple-cd-section-hint">What to worry about first on this condition</span>
      </div>
      <div
        data-testid={`biggest-risk-card-${issue.id}`}
        data-severity={primary.severity}
        style={{
          background: sevTone.bg,
          border: `1px solid ${sevTone.border}`,
          borderRadius: 16,
          padding: "18px 20px",
          display: "flex", gap: 14, alignItems: "flex-start",
        }}
      >
        <HeroIcon size={28} weight="duotone" color={sevTone.chip} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mono" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", color: sevTone.text }}>
            {primary.eyebrow}
          </div>
          <div data-testid={`biggest-risk-headline-${issue.id}`} style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", marginTop: 4, letterSpacing: "-0.01em" }}>
            {primary.headline}
          </div>
          <div data-testid={`biggest-risk-why-${issue.id}`} style={{ marginTop: 10, padding: "10px 12px", background: "rgba(255,255,255,0.65)", borderRadius: 10, border: "1px solid rgba(15,42,81,0.08)" }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", color: "#475569" }}>WHY THIS MATTERS</div>
            <div style={{ fontSize: 12.5, color: "#0f172a", lineHeight: 1.55, marginTop: 4 }}>{primary.why}</div>
          </div>
          {primary.detail && (
            <div data-testid={`biggest-risk-detail-${issue.id}`} style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.6, marginTop: 10 }}>
              {primary.detail}
            </div>
          )}
        </div>
      </div>
      <SecondaryPrimaryPicker issue={issue} allIssues={allIssues} updateIssue={updateIssue} />
      {secondaries.length > 0 && (
        <div data-testid={`secondary-risks-${issue.id}`} style={{ marginTop: 12, padding: "12px 14px", background: "rgba(15,42,81,0.03)", border: "1px solid rgba(15,42,81,0.10)", borderRadius: 12 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", color: "#475569" }}>OTHER THINGS TO REVIEW</div>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#0f172a" }}>
            {secondaries.map((s, i) => (
              <li key={i} data-testid={`secondary-risk-${i}`} style={{ fontSize: 12.5, lineHeight: 1.55, marginBottom: 4 }}>
                <strong>{s.title}.</strong> {s.body}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 5 — RATING BUILDER
//
// Shows the DBQ, CFR reference, rating tier descriptor, and the condition-
// specific C&P prep bullets. Pulls from getConditionResources so the content
// is always tied to THIS condition's diagnostic code — no template leakage.
// ---------------------------------------------------------------------------

function RatingBuilderSection({ issue, updateIssue, openMentalHealth }) {
  const res = useMemo(() => getConditionResources(issue.code, issue.system), [issue.code, issue.system]);
  const isMental = (issue.system || "").toLowerCase() === "mental";
  const sysLabel = bodySystems.find((s) => s.id === issue.system)?.label || issue.system;
  return (
    <section className="simple-cd-rating-builder" data-testid={`rating-builder-${issue.id}`}>
      <div className="simple-cd-section-head">
        <h3 className="simple-cd-section-title">Rating Builder</h3>
        <span className="simple-cd-section-hint">DBQ, rating reference, and C&P prep for this condition</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <a
          href={res.dbqUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`rating-builder-dbq-${issue.id}`}
          style={{ display: "block", padding: "12px 14px", background: "rgba(15,42,81,0.04)", border: "1px solid rgba(15,42,81,0.16)", borderRadius: 12, textDecoration: "none", color: "inherit" }}
        >
          <div className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", color: "#475569" }}>DBQ FORM</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0F2A51", marginTop: 4 }}>{res.dbqName} →</div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>For {sysLabel} conditions</div>
        </a>
        <a
          href={res.cfrUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`rating-builder-cfr-${issue.id}`}
          style={{ display: "block", padding: "12px 14px", background: "rgba(15,42,81,0.04)", border: "1px solid rgba(15,42,81,0.16)", borderRadius: 12, textDecoration: "none", color: "inherit" }}
        >
          <div className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", color: "#475569" }}>RATING CRITERIA</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0F2A51", marginTop: 4 }}>{res.cfrRef || "38 CFR Part 4"} →</div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>View official rating tiers on eCFR</div>
        </a>
      </div>

      {/* Estimated rating + functional impact paragraph (the two fields that drive the rating tier) */}
      <div style={{ padding: "14px 16px", background: "rgba(255,255,255,0.7)", border: "1px solid rgba(15,42,81,0.10)", borderRadius: 12, marginBottom: 12 }}>
        <div className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", color: "#475569" }}>SELECTED RATING TARGET</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
          <input
            type="number"
            min={0}
            max={100}
            step={10}
            value={issue.estimatedRating || 0}
            onChange={(e) => updateIssue(issue.id, { estimatedRating: Number(e.target.value) })}
            data-testid={`rating-builder-target-${issue.id}`}
            className="field-input"
            style={{ width: 90, fontSize: 16, fontWeight: 700 }}
          />
          <span style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>%</span>
          <span style={{ fontSize: 12, color: "#64748b", fontStyle: "italic", flex: 1 }}>
            Final rating depends on VA review, C&amp;P exam findings, and the full record.
          </span>
        </div>
        <label style={{ display: "block", marginTop: 12 }}>
          <div className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", color: "#475569" }}>FUNCTIONAL IMPACT — IN YOUR OWN WORDS</div>
          <textarea
            value={issue.functionalImpact || ""}
            onChange={(e) => updateIssue(issue.id, { functionalImpact: e.target.value })}
            placeholder={isMental
              ? "Describe occupational and social impairment, sleep, panic episodes, medication, and any deficiencies in work, family, judgment, thinking, or mood. This drives your § 4.130 tier."
              : "Describe how this condition limits you on a typical day — sitting, lifting, standing, attention, work tasks. This drives your rating tier."}
            data-testid={`rating-builder-impact-${issue.id}`}
            className="field-input field-textarea"
            style={{ marginTop: 6, minHeight: 84 }}
          />
        </label>
      </div>

      {res.cpPrep?.length > 0 && (
        <div data-testid={`rating-builder-cprep-${issue.id}`} style={{ padding: "14px 16px", background: "rgba(15,42,81,0.04)", border: "1px solid rgba(15,42,81,0.10)", borderRadius: 12 }}>
          <div className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", color: "#475569" }}>C&amp;P EXAM PREP — {res.cfrRef || "FACTUAL MEMORY AIDS"}</div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4, fontStyle: "italic" }}>
            Memory aids, not a script. Be honest, specific, and consistent with the records.
          </div>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#0f172a" }}>
            {res.cpPrep.map((tip, i) => (
              <li key={i} style={{ fontSize: 12.5, lineHeight: 1.55, marginBottom: 4 }}>{tip}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}





function NextBestActionBox({ issueId, allIssues, evidence, waiverCtx, onJump }) {
  const action = useMemo(
    // CRITICAL: scope to the currently-selected condition so the NBA card
    // never recommends a fix on a different condition. Previously this
    // accepted all issues and could surface e.g. spine guidance while the
    // veteran was looking at Adjustment Disorder.
    () => getNextBestAction(allIssues, evidence, waiverCtx, { restrictToIssueId: issueId }),
    [issueId, allIssues, evidence, waiverCtx]
  );
  if (!action) {
    return (
      <div className="nba-box nba-box--done" data-testid="nba-box">
        <CheckCircle size={22} weight="duotone" />
        <div>
          <div className="nba-eyebrow">YOU'RE READY</div>
          <div className="nba-headline">All four proof pillars on this condition are complete.</div>
          <p className="nba-copy">Re-read your statements, run the C&amp;P prep, and add the Master PDF to your packet.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="nba-box" data-testid="nba-box">
      <Lightning size={22} weight="duotone" />
      <div className="nba-body">
        <div className="nba-eyebrow">NEXT BEST ACTION</div>
        <div className="nba-headline" data-testid="nba-headline">{action.headline}</div>
        <p className="nba-copy" data-testid="nba-copy">{action.copy}</p>
      </div>
      <button
        type="button"
        className="btn btn--accent nba-cta"
        onClick={() => onJump?.(action)}
        data-testid="nba-cta"
      >
        Take me there <ArrowRight size={13} weight="bold" />
      </button>
    </div>
  );
}

function PillarCard({
  issue, el, evidence, waiverCtx,
  onMarkTouched, onMarkReviewed, onAddEvidence, onDeleteEvidence,
}) {
  const Icon = PILLAR_ICON[el.id] || FileText;
  const state = pillarStateForIssue(issue, el.id, evidence, waiverCtx);
  const waiver = el.id === "nexus" ? nexusWaiver(waiverCtx?.workspace, waiverCtx?.master526ez) : { waived: false };
  const items = evidence.filter((ev) => ev.issueIds?.includes(issue.id) && ev.element === el.id);
  const [open, setOpen] = useState(false);

  function handleView() {
    onMarkTouched?.(issue.id, el.id);
    setOpen((p) => !p);
  }
  function handleAdd() {
    onMarkTouched?.(issue.id, el.id);
    onAddEvidence?.(issue.id, el.id);
  }
  function handleReview() {
    onMarkReviewed?.(issue.id, el.id);
  }

  return (
    <div className={`pillar-card pillar-card--${state}`} data-testid={`pillar-card-${issue.id}-${el.id}`}>
      <div className="pillar-card-head">
        <div className="pillar-card-icon" aria-hidden="true"><Icon size={18} weight="duotone" /></div>
        <div className="pillar-card-meta">
          <div className="pillar-card-title">{el.label}</div>
          <div className="pillar-card-help">
            {waiver.waived ? waiver.reason : el.help}
          </div>
        </div>
        <StatusPill state={state} testid={`pillar-pill-${issue.id}-${el.id}`} />
      </div>
      <div className="pillar-card-row">
        <span className="pillar-card-count" data-testid={`pillar-count-${issue.id}-${el.id}`}>
          {items.length} {items.length === 1 ? "item" : "items"} attached
        </span>
        <div className="pillar-card-actions">
          {items.length > 0 && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={handleView}
              data-testid={`pillar-view-${issue.id}-${el.id}`}
            >
              <Eye size={12} weight="bold" /> {open ? "Hide" : "View"}
            </button>
          )}
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={handleAdd}
            data-testid={`pillar-add-${issue.id}-${el.id}`}
          >
            <Plus size={12} weight="bold" /> Add
          </button>
          {items.length > 0 && state === PILLAR_STATE.needsReview && (
            <button
              type="button"
              className="btn btn--accent btn--sm"
              onClick={handleReview}
              data-testid={`pillar-review-${issue.id}-${el.id}`}
            >
              <CheckCircle size={12} weight="bold" /> Mark reviewed
            </button>
          )}
        </div>
      </div>
      {open && items.length > 0 && (
        <ul className="pillar-card-items" data-testid={`pillar-items-${issue.id}-${el.id}`}>
          {items.map((it) => (
            <li key={it.id} className="pillar-card-item">
              <FileText size={11} weight="bold" />
              <span className="pillar-card-item-title">{it.title || it.kind || "Evidence item"}</span>
              {it.dated && <span className="pillar-card-item-date mono">{it.dated}</span>}
              <button
                type="button"
                className="icon-btn pillar-card-item-del"
                onClick={() => onDeleteEvidence?.(it.id)}
                title="Remove this item"
                data-testid={`pillar-item-del-${it.id}`}
              >
                <Trash size={11} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SimpleConditionDetail({
  issue, allIssues, evidence, waiverCtx,
  updateIssue, deleteIssue,
  addEvidence, deleteEvidence,
  setActiveView,
  onBack, onSwitchToAdvanced,
}) {
  const sysLabel = bodySystems.find((s) => s.id === issue.system)?.label || issue.system;
  const officialName = conditionLibrary.find((c) => c.code === issue.code)?.name || "";
  const showOfficialName = officialName && officialName.toLowerCase() !== (issue.claimedAs || "").toLowerCase();

  function markTouched(issueId, elementId) {
    updateIssue(issueId, { pillarTouched: { ...(issue.pillarTouched || {}), [elementId]: true } });
  }
  function markReviewed(issueId, elementId) {
    updateIssue(issueId, {
      pillarReviewed: { ...(issue.pillarReviewed || {}), [elementId]: true },
      pillarTouched: { ...(issue.pillarTouched || {}), [elementId]: true },
    });
  }

  function handleAddEvidence(issueId, elementId) {
    // Hand-off to the legacy add flow inside the existing tracker — we
    // emit a window event the parent listens to so the existing multi-add
    // modal (with title / file / dated / etc.) opens against this pillar.
    window.dispatchEvent(new CustomEvent("claimops:open-evidence-add", {
      detail: { issueId, elementId },
    }));
  }

  function jumpFromNba(action) {
    // The NBA is always scoped to the current condition (see
    // `restrictToIssueId` on getNextBestAction). So in nearly every case
    // the action.issueId === currently-viewed issue, and "Take me there"
    // means: scroll to the right pillar card AND momentarily highlight it
    // so the vet's eye lands on it.
    //
    // Falls back to the cross-condition CustomEvent when the action does
    // point to a different condition (defensive — currently unreachable).
    const tid = `pillar-card-${action.issueId}-${action.elementId}`;
    if (action.issueId === issue.id) {
      const el = document.querySelector(`[data-testid="${tid}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Brief highlight ring so the user sees where we landed.
        el.classList.add("pillar-card--flash");
        window.setTimeout(() => el.classList.remove("pillar-card--flash"), 1600);
        return;
      }
    }
    window.dispatchEvent(new CustomEvent("claimops:select-condition", {
      detail: { issueId: action.issueId, elementId: action.elementId },
    }));
  }

  return (
    <div className="simple-cd" data-testid={`simple-cd-${issue.id}`}>
      <div className="simple-cd-topbar">
        {onBack && (
          <button className="btn btn--ghost btn--sm" onClick={onBack} data-testid="simple-cd-back">
            <ArrowLeft size={12} weight="bold" /> Back to list
          </button>
        )}
        <button
          type="button"
          className="link-btn simple-cd-advanced-link"
          onClick={onSwitchToAdvanced}
          data-testid="simple-cd-advanced"
          title="See the full power-user layout for this condition"
        >
          Switch to Advanced View
        </button>
      </div>

      <section className="simple-cd-header" data-testid={`simple-cd-header-${issue.id}`}>
        <div className="simple-cd-header-main">
          <div className="simple-cd-eyebrow mono">DC {issue.code}</div>
          <h2 className="simple-cd-title" data-testid={`simple-cd-title-${issue.id}`}>{issue.claimedAs || officialName || "Untitled condition"}</h2>
          {showOfficialName && (
            <div className="simple-cd-subtitle">VA library: {officialName}</div>
          )}
          <div className="simple-cd-meta">
            <span className="meta-pill meta-pill--system">{sysLabel}</span>
            <span className="meta-pill meta-pill--rating mono">{issue.estimatedRating || 0}% est.</span>
            <span className="meta-pill meta-pill--theory" title={issue.theory}>{THEORY_SHORT_LABEL[issue.theory] || issue.theory}</span>
            {issue.priority && (
              <span className={`meta-pill meta-pill--priority meta-pill--priority-${(issue.priority || "Medium").toLowerCase()}`}>{issue.priority} priority</span>
            )}
          </div>
        </div>
        <div className="simple-cd-header-actions">
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => setActiveView?.("statements")}
            data-testid={`simple-cd-statement-${issue.id}`}
          >
            <ChatCircle size={12} weight="bold" /> Statement
          </button>
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => setActiveView?.("forms")}
            data-testid={`simple-cd-forms-${issue.id}`}
          >
            <FileText size={12} weight="bold" /> Open in Forms
          </button>
          <button
            type="button"
            className="btn btn--danger btn--sm"
            onClick={() => deleteIssue?.(issue.id)}
            data-testid={`simple-cd-delete-${issue.id}`}
            title="Remove this condition"
          >
            <Trash size={12} weight="bold" />
          </button>
        </div>
      </section>

      {/* 2. BIGGEST RISK — single consolidated hero replacing the old
          PreExisting + ClaimRisk + MentalHealth + RecommendedUpload stack. */}
      <BiggestRiskHero
        issue={issue}
        allIssues={allIssues}
        evidence={evidence}
        waiverCtx={waiverCtx}
        updateIssue={updateIssue}
        master526ez={waiverCtx?.master526ez}
      />

      {/* 3. NEXT BEST ACTION — scoped to THIS condition only, so the copy
          is always condition-specific (no template leakage to siblings). */}
      <NextBestActionBox
        issueId={issue.id}
        allIssues={allIssues}
        evidence={evidence}
        waiverCtx={waiverCtx}
        onJump={jumpFromNba}
      />

      {/* 4. EVIDENCE PILLARS — the four-pillar attach grid. */}
      <section className="simple-cd-pillars">
        <div className="simple-cd-section-head">
          <h3 className="simple-cd-section-title">Evidence pillars</h3>
          <span className="simple-cd-section-hint">Click View to see items, Add to attach new ones</span>
        </div>
        <div className="pillar-card-grid">
          {evidenceElements.map((el) => (
            <PillarCard
              key={el.id}
              issue={issue}
              el={el}
              evidence={evidence}
              waiverCtx={waiverCtx}
              onMarkTouched={markTouched}
              onMarkReviewed={markReviewed}
              onAddEvidence={handleAddEvidence}
              onDeleteEvidence={deleteEvidence}
            />
          ))}
        </div>
      </section>

      {/* Pre-existing-condition banner stays available but demoted under the
          pillar section so it doesn't compete with the Biggest Risk hero. */}
      <PreExistingFlagBanner
        issue={issue}
        master526ez={waiverCtx?.master526ez}
        onSetAggravationTheory={() => updateIssue(issue.id, { theory: THEORIES.aggravation })}
      />

      {/* Mental-health rating helper — surfaces only when the condition is
          mental-health. Lives here, under the pillars, near the Rating
          Builder where the documentation prompts are most actionable. */}
      <MentalHealthHelper issue={issue} />

      {/* 5. RATING BUILDER — condition-specific DBQ, CFR reference, rating
          tier input, functional-impact paragraph, and C&P prep bullets.
          (Was previously called "Rating and DBQ Map".) */}
      <RatingBuilderSection issue={issue} updateIssue={updateIssue} />

      {/* 6. EXPORT PACKAGE — one-click hand-off to the Export view. */}
      <section className="simple-cd-export" data-testid={`export-section-${issue.id}`}>
        <div className="simple-cd-section-head">
          <h3 className="simple-cd-section-title">Export package</h3>
          <span className="simple-cd-section-hint">Generate the filing-ready Claim Readiness Brief</span>
        </div>
        <div style={{ padding: "16px 18px", background: "rgba(15,42,81,0.04)", border: "1px solid rgba(15,42,81,0.14)", borderRadius: 14, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <FileText size={24} weight="duotone" color="#0F2A51" />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Master Claim Readiness Brief</div>
            <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
              15-section PDF covering every claimed condition — bring to your VSO, attach to your file.
            </div>
          </div>
          <button
            type="button"
            className="btn btn--accent"
            data-testid={`export-cta-${issue.id}`}
            onClick={() => setActiveView?.("export")}
          >
            Open Export <ArrowRight size={13} weight="bold" />
          </button>
        </div>
      </section>
    </div>
  );
}
